import { useTranslation } from '@jq4race/i18n';
import { LocationPicker, type LocationPickerLocation } from '@jq4race/ui';
import { MapPin } from 'lucide-react';
import { useState } from 'react';

export interface LocationSwitcherProps {
  /** Active locations the supervisor is assigned to (already filtered by the caller). */
  locations: LocationPickerLocation[];
  activeLocationId: string | null;
  /** Called only when the supervisor picks a location different from `activeLocationId`. */
  onLocationChange: (id: string) => void;
}

/**
 * Topbar control that lets a supervisor with 2+ assigned locations see and
 * change which one they're operating in. Hidden entirely with 0 or 1
 * location — there is nothing to switch to.
 *
 * Reuses `@jq4race/ui`'s `LocationPicker` in its non-forced mode (dismissible,
 * `onDismiss` provided) — the forced variant stays reserved for the
 * mandatory first-pick flow in `AppShell`.
 */
export function LocationSwitcher({
  locations,
  activeLocationId,
  onLocationChange,
}: LocationSwitcherProps) {
  const { t } = useTranslation('app-shell');
  const [open, setOpen] = useState(false);

  if (locations.length < 2) return null;

  const activeName = locations.find((l) => l.id === activeLocationId)?.name;
  const displayName = activeName ?? t('locationSwitcher.unknownLocation');
  const accessibleLabel = activeName
    ? t('locationSwitcher.buttonLabel', { name: activeName })
    : t('locationSwitcher.unknownLocation');

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={accessibleLabel}
        title={accessibleLabel}
        className="inline-flex h-9 max-w-[12rem] items-center gap-1.5 rounded-md border border-jq-sand/70 bg-jq-surface-0 px-3 text-[13px] font-medium text-jq-graphite transition hover:border-jq-navy/30 hover:text-jq-navy"
      >
        <MapPin className="h-4 w-4 shrink-0 text-jq-stone" strokeWidth={1.5} />
        <span className="truncate">{displayName}</span>
      </button>

      {open && (
        <LocationPicker
          locations={locations}
          selectedId={activeLocationId ?? undefined}
          onDismiss={() => setOpen(false)}
          labels={{
            title: t('locationPicker.title'),
            description: t('locationPicker.description'),
            emptyDescription: t('locationPicker.emptyDescription'),
            noLocations: t('locationPicker.noLocations'),
            changeAnytime: t('locationPicker.changeAnytime'),
          }}
          onSelect={(id) => {
            setOpen(false);
            if (id !== activeLocationId) onLocationChange(id);
          }}
        />
      )}
    </>
  );
}
